/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// sweetalert2 components

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

import { getData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Inactive" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Active" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

/**
 *
 * @returns
 */
function UsersLogsList() {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform } = controller;
  const [loading, setLoading] = useState(true);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "id", accessor: "id", width: "100px" },
      { Header: "username", accessor: "username", width: "100px" },
      { Header: "email", accessor: "email", width: "100px" },
      { Header: "ip address", accessor: "ip_address", width: "100px", Cell: makeLinkCell },
      {
        Header: "Date",
        accessor: "created_at_utc",
        width: "100px",
        Cell: formatDateStringUTCtoLocale,
      },
      { Header: "agent", accessor: "user_agent", width: "100px", Cell: wrapWordCell },
      { Header: "activity", accessor: "activity", width: "100px" },

      //   { Header: "roles", accessor: "roles", Cell: makeList, width: "120px" },
      //   { Header: "status", accessor: "status", width: "120px", Cell: makeStatusCell },
    ],

    rows: [],
  });

  function makeList({ value, ...rest }) {
    if (!value) return <></>;
    let listItems = value;

    return (
      <>
        {listItems.map((item) => {
          return (
            <SoftBadge
              style={{ marginRight: "3px" }}
              key={item}
              variant="gradient"
              color={item === "inplay" ? "info" : "secondary"}
              size="xs"
              badgeContent={item}
              container
            />
          );
        })}
      </>
    );
  }

  function wrapWordCell({ value, ...rest }) {
    let headerName = rest.column.id;
    return <div className={headerName + "-cell"}>{value}</div>;
  }

  function formatDateStringUTCtoLocale({ value, ...rest }) {
    if (!value) return "-";
    let date = new Date(value);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

    let dateformatted = day + "/" + month + "/" + date.getFullYear() + " " + hours + ":" + minutes;
    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;
    return dateformatted;
  }

  function makeLinkCell({ value, ...rest }) {
    return (
      <>
        <Button onClick={(e) => searchIp(e, value)}>{value}</Button>
      </>
    );
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function searchIp(e, ip) {
    e.preventDefault();
    var q = ip;
    window.open("https://ipinfo.io/" + q);
  }

  /**
   *
   */
  async function initData() {
    const userData = getUserData();
    let userId = userData.id;

    let getLogsEndpoint = `/api/v1/useractivities/${userId}`;
    setLoading(true);
    let response = await getData(
      getLogsEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
    }

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  let navigate = useNavigate();
  
  useEffect(() => {
    initData();
    return () => {
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Users&apos; Logs
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the Users&apos; Logs
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            hiddenColumns={["activity"]}
            canSearch
            openBackDrop={loading}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default UsersLogsList;
